import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import SendIcon from '@material-ui/icons/Send';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import ImageIcon from '@material-ui/icons/Image';
import CloseIcon from '@material-ui/icons/Close';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import {
  COMPANY_SIGNED_IN_BACKGROUND_COLOR,
  COMPANY_STATION_ID,
  GENERAL_TEXT_COLOR,
  BASE_S3,
  BASE_API,
  USER_TOKEN,
  BUTTON_BACKGROUND_COLOR,
  BUTTON_TEXT_COLOR,
} from '../../../../../config/consts';
import {
  getClassifiedAd,
  createComment,
  deleteComment,
  deleteClassifiedAd,
} from './classifiedAdsActions';
import LoadingProgress from '../../../../../common/components/progress/loading';
import {
  DetailContainer,
  DetailCard,
  DetailImageCarousel,
  CarouselImage,
  CarouselButton,
  DetailContent,
  UserInfo,
  UserAvatar,
  CommentSection,
  CommentItem,
  CommentInput,
  CategoryChip,
  NoImagePlaceholder,
} from './styles';

const btnBg = BUTTON_BACKGROUND_COLOR || GENERAL_TEXT_COLOR;
const btnText = BUTTON_TEXT_COLOR || '#fff';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const InitialsAvatar = ({ name, size = 40 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: btnBg,
      color: btnText,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.4,
      fontWeight: 600,
      flexShrink: 0,
    }}
  >
    {getInitials(name)}
  </div>
);

function ClassifiedAdDetail(props) {
  const [currentImage, setCurrentImage] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    category_id: '',
    cel: '',
    email: '',
  });
  const [editImages, setEditImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const editFileRef = useRef(null);
  const adId = props.match.params.id;

  const getHeaders = () => ({
    Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
  });

  const fetchAd = () => {
    axios
      .get(`${BASE_API}/classified-ads/${adId}`, { headers: getHeaders() })
      .then((response) => {
        setAd(response.data);
        setLoading(false);
      })
      .catch((e) => {
        console.log('[ClassifiedAd] Error:', e.response?.status, e.response?.data || e.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAd();
    axios
      .get(`${BASE_API}/classified-ads-all-categories`, { headers: getHeaders() })
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  const getImageUrl = (image) => {
    if (!image || image.startsWith('file:///')) return null;
    if (image.startsWith('http')) return image;
    return `${BASE_S3}/${image}`;
  };

  const validImages = ad
    ? (ad.images || []).filter((i) => i.image && !i.image.startsWith('file:///'))
    : [];

  const handlePrevImage = () => {
    if (validImages.length > 0) {
      setCurrentImage((prev) =>
        prev === 0 ? validImages.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (validImages.length > 0) {
      setCurrentImage((prev) =>
        prev === validImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleSendComment = () => {
    if (commentText.trim()) {
      setCommentSubmitting(true);
      axios
        .post(
          `${BASE_API}/comment-classified-ads/${adId}`,
          { description: commentText },
          { headers: getHeaders() }
        )
        .then(() => {
          toastr.success('Sucesso', 'Comentário enviado!');
          setCommentText('');
          setCommentSubmitting(false);
          fetchAd();
        })
        .catch(() => {
          toastr.error('Erro', 'Erro ao enviar comentário');
          setCommentSubmitting(false);
        });
    }
  };

  const handleDeleteAd = () => {
    if (window.confirm('Tem certeza que deseja excluir este anúncio?')) {
      axios
        .delete(`${BASE_API}/classified-ads/${adId}`, { headers: getHeaders() })
        .then(() => {
          toastr.success('Sucesso', 'Anúncio removido com sucesso!');
          props.history.push('/homepage/classificados');
        })
        .catch(() => {
          toastr.error('Erro', 'Erro ao remover anúncio');
        });
    }
  };

  const openEditModal = () => {
    setEditForm({
      title: ad.title || '',
      description: ad.description || '',
      category_id: ad.category_id || '',
      cel: ad.cel || '',
      email: ad.email || '',
    });
    setEditImages(
      validImages.map((img) => ({ id: img.id, image: img.image, isExisting: true }))
    );
    setNewImages([]);
    setEditOpen(true);
  };

  const handleEditFormChange = (field) => (e) => {
    setEditForm({ ...editForm, [field]: e.target.value });
  };

  const handleEditImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const total = editImages.length + newImages.length + files.length;
    if (total > 5) {
      toastr.warning('Atenção', 'Máximo de 5 imagens por anúncio');
      return;
    }
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setNewImages((prev) => [
          ...prev,
          { file, preview: ev.target.result, base64: ev.target.result },
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeExistingImage = (index) => {
    setEditImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImageToS3 = async (base64Data) => {
    const response = await axios.post(
      `${BASE_API}/upload-image-to-s3`,
      { base64: base64Data },
      { headers: getHeaders() }
    );
    return response.data.url;
  };

  const handleEditSubmit = async () => {
    if (!editForm.title.trim()) {
      toastr.warning('Atenção', 'Informe o título');
      return;
    }
    if (!editForm.description.trim()) {
      toastr.warning('Atenção', 'Informe a descrição');
      return;
    }

    setEditSubmitting(true);
    try {
      const images = editImages.map((img) => ({ id: img.id, image: img.image }));
      for (const img of newImages) {
        const s3Path = await uploadImageToS3(img.base64);
        images.push({ image: s3Path });
      }

      await axios.put(
        `${BASE_API}/update-ads/${adId}`,
        {
          station_id: COMPANY_STATION_ID,
          title: editForm.title,
          description: editForm.description,
          category_id: editForm.category_id,
          cel: editForm.cel,
          email: editForm.email,
          images,
        },
        { headers: getHeaders() }
      );

      toastr.success('Sucesso', 'Anúncio atualizado!');
      setEditOpen(false);
      fetchAd();
    } catch (e) {
      console.log('[ClassifiedAd] Edit error:', e.response?.data || e.message);
      toastr.error('Erro', 'Erro ao atualizar anúncio');
    }
    setEditSubmitting(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <section
        style={{
          backgroundColor: COMPANY_SIGNED_IN_BACKGROUND_COLOR,
          minHeight: '100vh',
        }}
      >
        <DetailContainer>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '60px 0',
            }}
          >
            <LoadingProgress>Carregando anúncio...</LoadingProgress>
          </div>
        </DetailContainer>
      </section>
    );
  }

  if (!ad) {
    return (
      <section
        style={{
          backgroundColor: COMPANY_SIGNED_IN_BACKGROUND_COLOR,
          minHeight: '100vh',
        }}
      >
        <DetailContainer>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => props.history.push('/homepage/classificados')}
            style={{ marginBottom: 16, color: GENERAL_TEXT_COLOR }}
          >
            Voltar
          </Button>
          <Typography variant="h6" style={{ color: '#999', textAlign: 'center' }}>
            Anúncio não encontrado
          </Typography>
        </DetailContainer>
      </section>
    );
  }

  const hasImages = validImages.length > 0;
  const isOwner = ad.user_id === props.auth.user.id;

  return (
    <section
      style={{
        backgroundColor: COMPANY_SIGNED_IN_BACKGROUND_COLOR,
        minHeight: '100vh',
      }}
    >
      <DetailContainer>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => props.history.push('/homepage/classificados')}
          style={{
            marginBottom: 16,
            color: btnBg,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Voltar aos classificados
        </Button>

        <DetailCard>
          {hasImages ? (
            <DetailImageCarousel>
              <CarouselImage
                src={getImageUrl(validImages[currentImage]?.image)}
                alt={ad.title}
              />
              {validImages.length > 1 && (
                <>
                  <CarouselButton direction="left" onClick={handlePrevImage}>
                    <ChevronLeftIcon />
                  </CarouselButton>
                  <CarouselButton direction="right" onClick={handleNextImage}>
                    <ChevronRightIcon />
                  </CarouselButton>
                </>
              )}
              {validImages.length > 1 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 6,
                  }}
                >
                  {validImages.map((_, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor:
                          idx === currentImage
                            ? '#fff'
                            : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                      }}
                      onClick={() => setCurrentImage(idx)}
                    />
                  ))}
                </div>
              )}
            </DetailImageCarousel>
          ) : (
            <DetailImageCarousel>
              <NoImagePlaceholder>
                <ImageIcon style={{ fontSize: 80, color: '#ccc' }} />
              </NoImagePlaceholder>
            </DetailImageCarousel>
          )}

          <DetailContent>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  style={{
                    fontWeight: 600,
                    color: GENERAL_TEXT_COLOR,
                    marginBottom: 4,
                  }}
                >
                  {ad.title}
                </Typography>
              </div>
              {isOwner && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <IconButton
                    onClick={openEditModal}
                    style={{ color: btnBg }}
                    title="Editar anúncio"
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={handleDeleteAd}
                    style={{ color: '#e53935' }}
                    title="Excluir anúncio"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>

            <Typography
              variant="caption"
              style={{ color: '#999', display: 'block', marginBottom: 16 }}
            >
              Publicado em {formatDate(ad.createdAt)} às{' '}
              {formatTime(ad.createdAt)}
            </Typography>

            <Typography
              variant="body1"
              style={{
                color: '#555',
                marginBottom: 24,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
              }}
            >
              {ad.description}
            </Typography>

            {(ad.cel || ad.email) && (
              <div
                style={{
                  background: '#f5f5f5',
                  borderRadius: 12,
                  padding: '16px 20px',
                  marginBottom: 24,
                  border: '1px solid #eee',
                }}
              >
                <Typography
                  variant="subtitle2"
                  style={{ marginBottom: 10, color: GENERAL_TEXT_COLOR }}
                >
                  Contato
                </Typography>
                {ad.cel && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <PhoneIcon style={{ fontSize: 18, color: btnBg }} />
                    <Typography variant="body2" style={{ color: '#555' }}>
                      {ad.cel}
                    </Typography>
                  </div>
                )}
                {ad.email && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <EmailIcon style={{ fontSize: 18, color: btnBg }} />
                    <Typography variant="body2" style={{ color: '#555' }}>
                      {ad.email}
                    </Typography>
                  </div>
                )}
              </div>
            )}

            <UserInfo>
              {ad.user?.image ? (
                <UserAvatar
                  src={`${BASE_S3}/${ad.user.image}`}
                  alt={ad.user?.name}
                />
              ) : (
                <InitialsAvatar name={ad.user?.name} size={40} />
              )}
              <div>
                <Typography
                  variant="subtitle2"
                  style={{ color: GENERAL_TEXT_COLOR }}
                >
                  {ad.user?.name}
                </Typography>
                <Typography variant="caption" style={{ color: '#999' }}>
                  Anunciante
                </Typography>
              </div>
            </UserInfo>

            <CommentSection>
              <Typography
                variant="h6"
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: GENERAL_TEXT_COLOR,
                  marginBottom: 16,
                }}
              >
                Comentários{' '}
                {ad.comments && ad.comments.length > 0
                  ? `(${ad.comments.length})`
                  : ''}
              </Typography>

              <CommentInput>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Escreva um comentário..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleSendComment();
                  }}
                  disabled={commentSubmitting}
                />
                <IconButton
                  onClick={handleSendComment}
                  disabled={
                    !commentText.trim() ||
                    commentSubmitting
                  }
                  style={{ color: btnBg }}
                >
                  <SendIcon />
                </IconButton>
              </CommentInput>

              {ad.comments && ad.comments.length > 0 ? (
                ad.comments.map((comment) => (
                  <CommentItem key={comment.id}>
                    {comment.user?.image ? (
                      <UserAvatar
                        src={`${BASE_S3}/${comment.user.image}`}
                        alt={comment.user?.name}
                        style={{ width: 32, height: 32 }}
                      />
                    ) : (
                      <InitialsAvatar name={comment.user?.name} size={32} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontSize: 13,
                            color: GENERAL_TEXT_COLOR,
                          }}
                        >
                          {comment.user?.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ color: '#bbb' }}
                        >
                          {formatDate(comment.createdAt)}
                        </Typography>
                      </div>
                      <Typography
                        variant="body2"
                        style={{ color: '#666', marginTop: 2 }}
                      >
                        {comment.description}
                      </Typography>
                    </div>
                  </CommentItem>
                ))
              ) : (
                <Typography
                  variant="body2"
                  style={{
                    color: '#bbb',
                    textAlign: 'center',
                    padding: '20px 0',
                  }}
                >
                  Nenhum comentário ainda. Seja o primeiro!
                </Typography>
              )}
            </CommentSection>
          </DetailContent>
        </DetailCard>
      </DetailContainer>

      <Dialog
        open={editOpen}
        onClose={() => !editSubmitting && setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ padding: '20px 28px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" style={{ fontWeight: 600, color: GENERAL_TEXT_COLOR }}>
              Editar Anúncio
            </Typography>
            <IconButton onClick={() => !editSubmitting && setEditOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: '16px 28px' }}>
          <TextField
            fullWidth
            label="Título"
            variant="outlined"
            size="small"
            value={editForm.title}
            onChange={handleEditFormChange('title')}
            style={{ marginBottom: 16 }}
            disabled={editSubmitting}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            fullWidth
            label="Descrição"
            variant="outlined"
            size="small"
            multiline
            rows={4}
            value={editForm.description}
            onChange={handleEditFormChange('description')}
            style={{ marginBottom: 16 }}
            disabled={editSubmitting}
          />
          <TextField
            fullWidth
            select
            label="Categoria"
            variant="outlined"
            size="small"
            value={editForm.category_id}
            onChange={handleEditFormChange('category_id')}
            style={{ marginBottom: 16 }}
            disabled={editSubmitting}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.category}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Telefone / WhatsApp"
            variant="outlined"
            size="small"
            value={editForm.cel}
            onChange={handleEditFormChange('cel')}
            style={{ marginBottom: 16 }}
            disabled={editSubmitting}
            placeholder="(00) 00000-0000"
          />
          <TextField
            fullWidth
            label="E-mail (opcional)"
            variant="outlined"
            size="small"
            value={editForm.email}
            onChange={handleEditFormChange('email')}
            style={{ marginBottom: 20 }}
            disabled={editSubmitting}
          />

          <Typography variant="subtitle2" style={{ marginBottom: 10, color: GENERAL_TEXT_COLOR }}>
            Imagens (máx. 5)
          </Typography>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            {editImages.map((img, idx) => (
              <div
                key={`existing-${idx}`}
                style={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '2px solid #e0e0e0',
                }}
              >
                <img
                  src={getImageUrl(img.image)}
                  alt={`img-${idx}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeExistingImage(idx)}
                  disabled={editSubmitting}
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    padding: 2,
                  }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </div>
            ))}
            {newImages.map((img, idx) => (
              <div
                key={`new-${idx}`}
                style={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: `2px solid ${btnBg}`,
                }}
              >
                <img
                  src={img.preview}
                  alt={`new-${idx}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeNewImage(idx)}
                  disabled={editSubmitting}
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    padding: 2,
                  }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </div>
            ))}
            {editImages.length + newImages.length < 5 && (
              <div
                onClick={() => !editSubmitting && editFileRef.current?.click()}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 8,
                  border: '2px dashed #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: editSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'border-color 0.2s',
                }}
              >
                <AddPhotoAlternateIcon style={{ color: '#bbb', fontSize: 28 }} />
              </div>
            )}
          </div>
          <input
            ref={editFileRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleEditImageSelect}
          />
        </DialogContent>
        <DialogActions style={{ padding: '12px 28px 20px' }}>
          <Button
            onClick={() => setEditOpen(false)}
            disabled={editSubmitting}
            style={{ textTransform: 'none', color: '#888' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editSubmitting}
            style={{
              backgroundColor: btnBg,
              color: btnText,
              textTransform: 'none',
              borderRadius: 8,
              minWidth: 120,
            }}
          >
            {editSubmitting ? (
              <CircularProgress size={20} style={{ color: btnText }} />
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    classifiedAds: state.classifiedAds,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    { getClassifiedAd, createComment, deleteComment, deleteClassifiedAd },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ClassifiedAdDetail);
